import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Upload, Save, RefreshCw, Sun, Moon, Trash2, Eye } from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

// Helper function to get full image URL - fixes URLs from old domains
const getFullImageUrl = (url) => {
  if (!url) return '';
  // Extract relative path if URL contains /api/uploads/
  if (url.includes('/api/uploads/')) {
    const match = url.match(/\/api\/uploads\/[^\s"']+/);
    if (match) {
      return `${window.location.origin}${match[0]}`;
    }
  }
  if (url.startsWith('http')) return url;
  return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
};

const CMSLogosPage = () => {
  const [logos, setLogos] = useState({
    logo_light: '',
    logo_dark: '',
    favicon: '',
    logo_email: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadLogos(); }, []);

  const loadLogos = async () => {
    try {
      const res = await api.get('/cms/logos');
      console.log('Logos loaded:', res.data);
      if (res.data) {
        setLogos({
          logo_light: res.data.logo_light || '',
          logo_dark: res.data.logo_dark || '',
          favicon: res.data.favicon || '',
          logo_email: res.data.logo_email || ''
        });
      }
    } catch (error) {
      console.error('Error loading logos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'logos');

    try {
      const res = await api.post('/cms/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setLogos({ ...logos, [field]: res.data.url });
      toast.success('Logo uploadé');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors de l\'upload');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/cms/logos', logos);
      toast.success('Logos enregistrés');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const clearLogo = (field) => {
    setLogos({ ...logos, [field]: '' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Logos du Site</h1>
          <p className="text-slate-500">Gérez les logos pour les différents contextes d'affichage</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-gradient-to-r from-cyan-500 to-blue-600">
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Logo Fond Clair */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sun className="w-5 h-5 text-amber-500" />
              Logo sur fond clair
            </CardTitle>
            <CardDescription>
              Utilisé sur les sections claires du site (header scrollé, pages blanches)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="aspect-video bg-white border-2 border-dashed border-slate-200 rounded-xl flex items-center justify-center p-8">
              {logos.logo_light ? (
                <img src={getFullImageUrl(logos.logo_light)} alt="Logo fond clair" className="max-h-full max-w-full object-contain" />
              ) : (
                <div className="text-center text-slate-400">
                  <Upload className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">Aucun logo configuré</p>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Input 
                value={logos.logo_light} 
                onChange={(e) => setLogos({ ...logos, logo_light: e.target.value })}
                placeholder="URL du logo ou upload"
                className="flex-1"
              />
              <label className="cursor-pointer">
                <input type="file" className="hidden" accept="image/*" onChange={(e) => handleUpload(e, 'logo_light')} />
                <div className="flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-slate-50 transition-colors h-10">
                  <Upload className="h-4 w-4" />
                </div>
              </label>
              {logos.logo_light && (
                <Button variant="outline" size="icon" onClick={() => clearLogo('logo_light')}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Logo Fond Sombre */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Moon className="w-5 h-5 text-indigo-500" />
              Logo sur fond sombre
            </CardTitle>
            <CardDescription>
              Utilisé sur les sections sombres (header initial, footer, back-office)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="aspect-video bg-slate-900 border-2 border-dashed border-slate-700 rounded-xl flex items-center justify-center p-8">
              {logos.logo_dark ? (
                <img src={getFullImageUrl(logos.logo_dark)} alt="Logo fond sombre" className="max-h-full max-w-full object-contain" />
              ) : (
                <div className="text-center text-slate-500">
                  <Upload className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">Aucun logo configuré</p>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Input 
                value={logos.logo_dark} 
                onChange={(e) => setLogos({ ...logos, logo_dark: e.target.value })}
                placeholder="URL du logo ou upload"
                className="flex-1"
              />
              <label className="cursor-pointer">
                <input type="file" className="hidden" accept="image/*" onChange={(e) => handleUpload(e, 'logo_dark')} />
                <div className="flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-slate-50 transition-colors h-10">
                  <Upload className="h-4 w-4" />
                </div>
              </label>
              {logos.logo_dark && (
                <Button variant="outline" size="icon" onClick={() => clearLogo('logo_dark')}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Favicon */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-cyan-500" />
              Favicon
            </CardTitle>
            <CardDescription>
              Icône affichée dans l'onglet du navigateur (format carré recommandé)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-slate-100 border-2 border-dashed border-slate-200 rounded-lg flex items-center justify-center">
                {logos.favicon ? (
                  <img src={getFullImageUrl(logos.favicon)} alt="Favicon" className="w-12 h-12 object-contain" />
                ) : (
                  <Upload className="w-6 h-6 text-slate-400" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm text-slate-600 mb-2">
                  Format recommandé : PNG ou ICO, 32x32 ou 64x64 pixels
                </p>
                <div className="flex gap-2">
                  <Input 
                    value={logos.favicon} 
                    onChange={(e) => setLogos({ ...logos, favicon: e.target.value })}
                    placeholder="URL du favicon"
                    className="flex-1"
                  />
                  <label className="cursor-pointer">
                    <input type="file" className="hidden" accept="image/*" onChange={(e) => handleUpload(e, 'favicon')} />
                    <div className="flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-slate-50 transition-colors h-10">
                      <Upload className="h-4 w-4" />
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logo Email */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Logo pour les emails
            </CardTitle>
            <CardDescription>
              Utilisé dans les templates d'emails (notifications, confirmations)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="aspect-[3/1] bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl flex items-center justify-center p-4">
              {logos.logo_email ? (
                <img src={getFullImageUrl(logos.logo_email)} alt="Logo email" className="max-h-full max-w-full object-contain" />
              ) : (
                <div className="text-center text-slate-400">
                  <Upload className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">Aucun logo email configuré</p>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Input 
                value={logos.logo_email} 
                onChange={(e) => setLogos({ ...logos, logo_email: e.target.value })}
                placeholder="URL du logo email"
                className="flex-1"
              />
              <label className="cursor-pointer">
                <input type="file" className="hidden" accept="image/*" onChange={(e) => handleUpload(e, 'logo_email')} />
                <div className="flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-slate-50 transition-colors h-10">
                  <Upload className="h-4 w-4" />
                </div>
              </label>
              {logos.logo_email && (
                <Button variant="outline" size="icon" onClick={() => clearLogo('logo_email')}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Preview Section */}
      <Card>
        <CardHeader>
          <CardTitle>Aperçu des logos</CardTitle>
          <CardDescription>Vérifiez l'apparence de vos logos dans les différents contextes</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Preview Header Clair */}
            <div>
              <p className="text-sm text-slate-500 mb-2">Header sur fond clair (après scroll)</p>
              <div className="bg-white border rounded-lg p-4 flex items-center gap-3">
                {logos.logo_light ? (
                  <img src={getFullImageUrl(logos.logo_light)} alt="Preview" className="h-10 object-contain" />
                ) : (
                  <div className="h-10 w-32 bg-slate-100 rounded flex items-center justify-center text-xs text-slate-400">Logo clair</div>
                )}
                <div className="flex gap-4 ml-auto text-sm text-slate-600">
                  <span>Services</span>
                  <span>Réalisations</span>
                  <span>Contact</span>
                </div>
              </div>
            </div>
            
            {/* Preview Header Sombre */}
            <div>
              <p className="text-sm text-slate-500 mb-2">Header sur fond sombre (initial)</p>
              <div className="bg-slate-900 border border-slate-700 rounded-lg p-4 flex items-center gap-3">
                {logos.logo_dark ? (
                  <img src={getFullImageUrl(logos.logo_dark)} alt="Preview" className="h-10 object-contain" />
                ) : (
                  <div className="h-10 w-32 bg-slate-800 rounded flex items-center justify-center text-xs text-slate-500">Logo sombre</div>
                )}
                <div className="flex gap-4 ml-auto text-sm text-slate-400">
                  <span>Services</span>
                  <span>Réalisations</span>
                  <span>Contact</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CMSLogosPage;
